import { Link } from "react-router-dom";
import { ShieldCheck, FileText } from "lucide-react";
import { staticUrl } from "@/lib/api";

const FALLBACKS = [
    "https://images.pexels.com/photos/7462340/pexels-photo-7462340.png",
    "https://images.pexels.com/photos/7108184/pexels-photo-7108184.jpeg",
    "https://images.pexels.com/photos/8371713/pexels-photo-8371713.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/fd94d242-06a5-4af0-93ba-0c141315848a/images/9bfbab4035d0ff1ff074c7aafc01237c0a827a32f863078809cf41a80484f384.png",
];

export function EncounterCard({ e, variant = "default" }) {
    const img = staticUrl(e.sketch_url) || FALLBACKS[(e.id || "").charCodeAt(0) % FALLBACKS.length] || FALLBACKS[0];
    const dateStr = (e.encounter_date || "").slice(0, 10) || "Date unknown";
    const loc = [e.location, e.country].filter(Boolean).slice(0, 2).join(", ") || "Location unknown";

    if (variant === "row") {
        return (
            <Link
                to={`/encounters/${e.id}`}
                className="flex gap-3 group py-3"
                data-testid={`encounter-row-${e.id}`}
            >
                <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-sm border" style={{ borderColor: "var(--ok-border)" }}>
                    <img src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm leading-tight line-clamp-2 group-hover:text-[var(--ok-accent)] transition-colors">
                        {e.title}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-1">
                        {dateStr} · {loc}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[10px] opacity-60 font-mono uppercase tracking-widest">
                        <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[var(--ok-accent)]" /> {e.source_category || "Witness Testimony"}</span>
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link
            to={`/encounters/${e.id}`}
            className="ok-card ok-card-hover overflow-hidden block group"
            data-testid={`encounter-card-${e.id}`}
        >
            <div className="aspect-[16/10] overflow-hidden relative">
                <img src={img} alt={e.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                {e.evidence_level && (
                    <div className="absolute top-3 right-3 ok-chip" style={{ background: "rgba(0,0,0,0.6)", color: "#fff", borderColor: "transparent" }}>
                        <ShieldCheck className="w-3 h-3 text-[var(--ok-accent)]" /> {String(e.evidence_level).replace(/_/g, " ")}
                    </div>
                )}
                {e.source_type === "historical" && (
                    <div className="absolute top-3 left-3 ok-chip" style={{ background: "var(--ok-accent)", color: "var(--ok-on-accent)", borderColor: "transparent" }}>
                        Historical
                    </div>
                )}
                {e.image_label && (
                    <div className="absolute bottom-3 left-3 font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm" style={{ background: "var(--ok-chip-overlay-bg)", color: "var(--ok-chip-overlay-color)" }}>
                        {e.image_label}
                    </div>
                )}
            </div>
            <div className="p-5 space-y-3">
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">
                    {dateStr} · {loc}
                </div>
                <h3 className="font-display font-bold text-lg leading-tight line-clamp-2 group-hover:text-[var(--ok-accent)] transition-colors">
                    {e.title}
                </h3>
                {e.ai_summary && (
                    <p className="text-sm opacity-70 line-clamp-2">{e.ai_summary}</p>
                )}
                <div className="flex items-center gap-4 pt-2 text-[10px] opacity-60 font-mono uppercase tracking-widest">
                    <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[var(--ok-accent)]" /> {e.source_category || "Witness Testimony"}</span>
                    {e.encounter_type && <span>{e.encounter_type}</span>}
                </div>
            </div>
        </Link>
    );
}
